/* Copyright 2016-2022
   Kabandy
   Author: Rajan Filomeno Coelho
 */

import moment from "moment";
import uuidPackage from "uuid";

export default class Common {
    constructor() {}

    /** Check existence value */
    static checkExistenceValue(str) {
        if (typeof str === "string") {
            if (
                str === "" ||
                !/[0-9a-zA-Z]+/.test(str) || // Check if it contains at least one letter or one number
                str.replace(/ /g, "").replace(/'/g, "") === "$"
            ) {
                return false;
                // Only "undefined" considered as an empty value (unknown kept as existing value)
            } else if (["undefined"].indexOf(str.trim().toLowerCase()) >= 0) {
                return false;
            } else {
                return true;
            }
        } else {
            if (str == null) {
                // Equal to null or undefined
                return false;
            } else {
                return true;
            }
        }
    }

    /** Convert a number into a string with leading zeros */
    static pad(num, size) {
        let output;
        try {
            // 24 zeros
            if (num < 0) {
                output = "-" + ("00000000000000000000000" + -num).substr(-(size - 1));
            } else {
                // "_" chosen over "+" for real numbers, because when sorting strings, "+" comes before "-", which
                // is not the case for "_"
                output = "_" + ("00000000000000000000000" + num).substr(-(size - 1));
            }
        } catch (err) {
            output = num.toString();
        }
        return output;
    }

    static sortArrayById(array = []) {
        let oArray = array.filter(cIter => !!cIter && typeof cIter.id === "string");
        oArray.sort(function(a, b) {
            if (a.id < b.id) {
                return -1;
            }
            if (a.id > b.id) {
                return 1;
            }
            return 0;
        });
        return oArray;
    }

    /** Sort iterations of an array by their creation (in the 'time' key) */
    static sortArrayByCreation(array = [], ascending = false) {
        let oArray = [].concat(array);
        oArray.sort(function(a, b) {
            // Comparison is made on the name, as defined in the content' field
            let timeA;
            let timeB;
            if (a.time && a.time.creation) timeA = a.time.creation;
            if (b.time && b.time.creation) timeB = b.time.creation;
            if (timeA < timeB) {
                return ascending ? -1 : 1;
            }
            if (timeA > timeB) {
                return ascending ? 1 : -1;
            }

            // dates must be equal
            return 0;
        });
        return oArray;
    }

    /** Sort object by its name (contained in 'content' sub-field) */
    static sortObjectByName(obj, ignoreCaps = false) {
        var array = [];
        var key;

        for (key in obj) {
            array.push(obj[key]);
        }

        return Common.sortArrayByName(array, ignoreCaps);
    }

    /** Sort array by its name (contained in 'content' sub-field) */
    static sortArrayByName(array = [], ignoreCaps = false) {
        let oArray = [].concat(array);
        oArray.sort(function(a, b) {
            // Comparison is made on the name, as defined in the content' field
            let nameA;
            let nameB;
            if (ignoreCaps) {
                nameA = a.content.name.toUpperCase();
                nameB = b.content.name.toUpperCase();
            } else {
                nameA = a.content.name;
                nameB = b.content.name;
            }
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }

            // names must be equal
            return 0;
        });
        return oArray;
    }

    /**
     * Will return the given string inside a span or not and cutting by "[...]" or ending by "..." depending of the args.
     * @param string The string to be shorten
     * @param N The number of character the string shouldn't exceed. Default is 30
     * @param type How the string will be cut. Can be "short","sub"
     */
    static shortString(string, N = 30, type = "short") {
        if (string) {
            var str = JSON.parse(JSON.stringify(string));
            //if the string is not that long just return it
            if (str.length <= N) return str;

            //if it is a substring too long
            if (type === "sub") {
                return str.substring(0, N - 3) + "...";
            }
            //if it is a substring too long
            else if (type === "short") {
                if (N < 7 || str < N) {
                    // Needs at least 7 characters, i.e. 'B[...]E' to have at least one Beginning and one End character
                    //if str is smaller than N then we can just return the string as it is
                    return str;
                } else {
                    var part = Math.floor((N - 5) / 2); // 5 corresponds to the five characters in [...]
                    if (str.length <= N) {
                        return str;
                    } else {
                        return str.slice(0, part) + "[...]" + str.slice(str.length - part, str.length);
                    }
                }
            } else return "";
        } else return "";
    }

    static normalizeString(iString = "") {
        const normalizedString = iString
            .normalize("NFD")
            .trim()
            .replace(/[\u0300-\u036f]/g, "")
            .toUpperCase();
        return normalizedString;
    }

    /** Formatted date, based on a timestamp defined by the time in milliseconds elapsed since the UNIX epoch */
    static formattedDate(timestamp, format) {
        var date;

        if (format === "date and time") {
            date = new moment(timestamp * 1000).format("MMMM Do YYYY, h:mm a");
        } else if (format === "date and time code") {
            let dateWithoutMinutes = new moment(timestamp * 1000).format("YYYY-MM-DD_HH");
            let dateMinutes = new moment(timestamp * 1000).format("mm");
            date = dateWithoutMinutes + "h" + dateMinutes;
        } else if (format === "date and time and seconds") {
            date = new moment(timestamp * 1000).format("MMMM Do YYYY, h:mm:ss a");
        } else if (format === "short") {
            date = new moment(timestamp * 1000).format("MMM Do YY");
        } else if (format === "concise") {
            date = new moment(timestamp * 1000).format("YYYY-MM-DD");
        } else if (format === "full") {
            date = new moment(timestamp * 1000).format("YYYY-MM-DD HH-mm-ss");
        } else if (format === "usual") {
            date = new moment(timestamp * 1000).format("DD-MM-YYYY");
        } else if (format === "written") {
            date = new moment(timestamp * 1000).format("Do MMMM YYYY");
        } else {
            // By default: only date
            date = new moment(timestamp * 1000).format("MMMM Do YYYY");
        }
        return date;
    }

    /** Format the string in order to be used in file names for instance */
    static formatName(str) {
        let maxSize = 60;

        if (typeof str !== "string") {
            return "";
        }
        return str.substring(0, maxSize).replace(/ /g, "_");
    }

    /** Get the items of the first array which are not in the second one */
    static excludedItems(array1, array2) {
        return array1.filter(function(el) {
            return array2.indexOf(el) < 0;
        });
    }

    /** Get the file extension of a file name
     *
     * If there is no extension (i.e. no dot in the file name),
     * an empty string "" is returned */
    static fileExtension(fileName) {
        if (/\./.test(fileName) === false) {
            return "";
        } else {
            return (
                "." +
                fileName
                    .split(".")
                    .pop()
                    .toLowerCase()
            );
        }
    }

    /** Convert a file size in bytes to a human-readable format */
    static fileSizeToString(size) {
        var i;

        if (size === 0) {
            return "0 KB";
        } else {
            i = Math.floor(Math.log(size) / Math.log(1024));
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + " " + ["B", "KB", "MB", "GB", "TB"][i];
        }
    }

    /** Check if a string corresponds to a number */
    static isANumber(n) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    /** Check that the string has an email format */
    static checkEmailFormat(str, validator) {
        return validator.isEmail(str);
    }

    /** Check the complexity of the password */
    static checkPasswordComplexity(str) {
        // Check only that there are at least 8 characters
        return /.{8,}/.test(str);
    }

    /** Convert "CamelCase" to "Camel Case" */
    static camelCaseToRegular(camelCaseString) {
        if (camelCaseString) {
            return camelCaseString
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .replace(/^./, function(str) {
                    return str.toUpperCase();
                })
                .replace(/Omni Class/g, "OmniClass"); // Exception for OmniClass one-word spelling
        } else {
            return null;
        }
    }

    /** Sort parameter by time (in object.content.time) */
    static sortParameterByNestedTime(parameter, timeField, reverseOrder) {
        var array = [];
        var key;

        if (timeField === null) {
            // Equal to null or undefined
            timeField = "creation";
        }

        for (key in parameter) {
            array.push(parameter[key]);
        }

        return array.sort(function(a, b) {
            var c = [a.content.time[timeField], b.content.time[timeField]].sort();

            if (reverseOrder) {
                if (c[0] === a.content.time[timeField]) {
                    return 1;
                } else {
                    return -1;
                }
            } else {
                if (c[0] === a.content.time[timeField]) {
                    return -1;
                } else {
                    return 1;
                }
            }
        });
    }

    /** Generate a Universal Unique Identifier, based on 'uuid' package */
    static uuid() {
        return uuidPackage.v4().replace(/-/g, ""); // v4 => random
    }

    static uuidV4() {
        return uuidPackage.v4();
    }

    /** Create a new ID for an entity */
    static newId(type) {
        type = type || "id";

        return type + "_" + this.uuid();
    }

    /** Decoding of strings stored in IFC, according to the buildingSMART IFC Implementation Guide */
    static ifcDecrypt(theString, entityNames) {
        var content;
        var decodedString;
        var ifcCommand;
        var index;
        var word;
        var words;

        if (theString) {
            // Remove simple quotes in strings if present
            theString = theString.replace(/(^"|^')|("$|'$)/g, "");

            if (/^IFC[A-Z]+$/.test(theString)) {
                // Simple IFC command name to be converted (e.g. IFCCURTAINWALL)
                if (entityNames) {
                    word = entityNames[theString];
                    if (word) {
                        // Found in the list of entity names in camel case format (e.g. IFCCURTAINWALL => IfcCurtainWall)
                        word = word[3] + word.slice(4, word.length);
                        word = Common.camelCaseToRegular(word); // => e.g. IfcCurtainWall => Curtain Wall
                        if (word === "Space") {
                            word = "Room";
                        } else if (word === "Building Element Proxy") {
                            word = "Other";
                        } else if (word === "Building Storey") {
                            word = "Floor";
                        } else if (word === "Beam Standard Case") {
                            word = "Beam";
                        } else if (word === "Column Standard Case") {
                            word = "Column";
                        } else if (word === "Door Standard Case") {
                            word = "Door";
                        } else if (word === "Member Standard Case") {
                            word = "Member";
                        } else if (word === "Plate Standard Case") {
                            word = "Plate";
                        } else if (word === "Slab Standard Case" || word === "Slab Elemented Case") {
                            word = "Slab";
                        } else if (word === "Wall Standard Case" || word === "Wall Elemented Case") {
                            word = "Wall";
                        } else if (word === "Window Standard Case") {
                            word = "Window";
                        }
                        return word;
                    } else {
                        return theString[3] + theString.slice(4, theString.length).toLowerCase();
                    }
                } else {
                    return theString[3] + theString.slice(4, theString.length).toLowerCase();
                }
            } else if (/^\..*\.$/.test(theString)) {
                // Some IFC values are defined as simple test strings (in capitals) between periods (e.g. .SWINGING.)
                // => they can be converted to lower-case (e.g. "Swinging")
                return theString[1] + theString.slice(2, theString.length - 1).toLowerCase();
            } else {
                // Reference: https://standards.buildingsmart.org/documents/Implementation/IFC2x_Model_Implementation_Guide_V2-0b.pdf
                // To account for non-ASCII characters, encoded as \X\... in the IFC file
                words = theString.toString().split(/\\X\\/);
                decodedString = words[0];
                if (words.length > 1) {
                    for (index = 1; index < words.length; index++) {
                        let codedCharacter = String.fromCharCode(parseInt(words[index].slice(0, 2), 16)).replace(/\r|\n/, " ");
                        decodedString += codedCharacter + words[index].slice(2, words[index].length);
                    }
                }
                // To account for non-ASCII characters, encoded as \X2\....\X0\ in the IFC file
                words = decodedString.toString().split(/\\X2\\/);
                decodedString = words[0];
                if (words.length > 1) {
                    for (index = 1; index < words.length; index++) {
                        let indexInside = 0;
                        while (words[index].slice(indexInside * 4, (indexInside + 1) * 4) !== "\\X0\\") {
                            decodedString += String.fromCharCode(parseInt(words[index].slice(indexInside * 4, (indexInside + 1) * 4), 16));
                            indexInside++;
                        }
                        decodedString += words[index].slice(indexInside * 4, words[index].length).replace(/\\X0\\/g, "");
                    }
                }
                // To account for non-ASCII characters, encoded as \X4\........\X0\ in the IFC file
                words = decodedString.toString().split(/\\X4\\/);
                decodedString = words[0];
                if (words.length > 1) {
                    for (index = 1; index < words.length; index++) {
                        let indexInside = 0;
                        while (words[index].slice(indexInside * 8, (indexInside + 1) * 8) !== "\\X0\\") {
                            decodedString += String.fromCharCode(parseInt(words[index].slice(indexInside * 8, (indexInside + 1) * 8), 16));
                            indexInside++;
                        }
                        decodedString += words[index].slice(indexInside * 8, words[index].length).replace(/\\X0\\/g, "");
                    }
                }
                // To account for non-ASCII characters, encoded as \S\... in the IFC file
                words = decodedString.toString().split(/\\S\\/);
                decodedString = words[0];
                if (words.length > 1) {
                    for (index = 1; index < words.length; index++) {
                        decodedString += String.fromCharCode(words[index][0].charCodeAt() + 128) + words[index].slice(1, words[index].length);
                    }
                }
                // Convert element values
                if (/^IFC[A-Z]+\(.*\)/.test(decodedString)) {
                    // regular expression to catch a possible IFC command name
                    ifcCommand = /^IFC[A-Z]+/.exec(decodedString)[0];
                    if (ifcCommand === "IFCBOOLEAN" || ifcCommand === "IFCLOGICAL") {
                        content = /IFC[A-Z]+\(([^)]+)\)/.exec(decodedString)[1]; // regular expression to catch the content of the IFC command
                        if (content.toUpperCase() === ".T.") {
                            decodedString = "True";
                        } else if (content.toUpperCase() === ".F.") {
                            decodedString = "False";
                        } else {
                            // .U. => UNKNOWN
                            decodedString = "Unknown";
                        }
                    } else {
                        word = decodedString.slice(ifcCommand.length + 1, decodedString.length - 1);
                        if (/^\..*\.$/.test(word)) {
                            // E.g. .SWINGING.
                            decodedString = word[1] + word.slice(2, word.length - 1).toLowerCase();
                        } else {
                            decodedString = decodedString.slice(ifcCommand.length + 1, decodedString.length - 1);
                        }
                    }
                }
                // Only properties that are not detected as valid url are formatted using camelCaseToRegular
                let result = this.isValidUrl(decodedString) ? decodedString : this.camelCaseToRegular(decodedString);
                if (typeof result === "string") {
                    result = result.replace(/^'/, "").replace(/'$/, ""); // Remove the surrounding single quotes if applicable
                    result = result.replace(/''/g, "'"); // Apostrophe in IFC marked as '' => should be replaced by a single '
                }
                return result;
            }
        } else {
            // Null or undefined input
            return "";
        }
    }

    // Detect if a property has the form of a URL
    static isValidUrl(iUrl) {
        let isUrl = false;

        if (/(((http|https):\/\/)|www\.)/.test(iUrl)) {
            isUrl = true;
        }

        return isUrl;
    }

    /* Sort properties by relations*/
    static sortPropertiesByRelation(propertiesContent) {
        let relations = {};

        Object.keys(propertiesContent).forEach(function(propertyKey) {
            if (propertiesContent[propertyKey].operation) {
                let propertiesExists = true;

                propertiesContent[propertyKey].operation.properties.forEach(propertyId => {
                    if (Object.keys(propertiesContent).indexOf(propertyId) < 0) {
                        propertiesExists = false;
                    }
                });
                if (propertiesExists) {
                    relations[propertyKey] = propertiesContent[propertyKey].operation.properties;
                }
            }
        });
        return this.sortItemsByRelation(Object.keys(propertiesContent), relations);
    }

    /* List items by relations */
    static sortItemsByRelation(listItems, relations) {
        var commandCreated;
        var index;
        var item;
        var listItemsInterm = Array.from(listItems);
        var orderedList = [];
        var relatedItem;

        // Loop until all properties  are created
        while (listItems.length > 0) {
            for (index = 0; index < listItems.length; index++) {
                item = listItems[index];

                // If no relations
                if (!relations[item]) {
                    orderedList.push(item);
                    listItemsInterm.splice(listItemsInterm.indexOf(item), 1);
                } else {
                    // Check if all related items have already been created
                    commandCreated = true;

                    for (var relatedIndex = 0; relatedIndex < relations[item].length; relatedIndex++) {
                        relatedItem = relations[item][relatedIndex];

                        if (orderedList.indexOf(relatedItem) < 0) {
                            commandCreated = false;
                        }
                    }

                    if (commandCreated === true) {
                        orderedList.push(item);
                        listItemsInterm.splice(listItemsInterm.indexOf(item), 1);
                    }
                }
            }
            listItems = Array.from(listItemsInterm);
        }
        return orderedList;
    }

    /** Sort a list of object GUID's by the name for the items in a list */
    static sortListItemsByName(items, itemIds) {
        var index;
        var list = [];
        var name;
        var names = {};
        var itemId;
        var sortedNames;

        for (index = 0; index < itemIds.length; index++) {
            itemId = itemIds[index];

            try {
                name = items[itemId].name;
            } catch (err) {
                name = "Undefined";
            }
            if (Object.keys(names).indexOf(name) < 0) {
                names[name] = [];
            }
            names[name].push(itemId);
        }
        sortedNames = Object.keys(names).sort();

        for (index = 0; index < sortedNames.length; index++) {
            name = sortedNames[index];
            list = list.concat(names[name].sort()); // Sort by object GUID within a subgroup
        }

        return list;
    }

    /** Convert  [secondes, nanoseconds] to ms */
    static secNSec2ms(iSecNSec) {
        return iSecNSec[0] * 1000 + iSecNSec[1] / 1000000;
    }

    /** Create ranges from a given length
     *
     * size = 17, chunkSize = 5 => output: [ [0, 4], [5, 9], [10, 14], [15, 16] ]
     *
     */
    static createRanges(size, chunkSize = 10) {
        if (size <= chunkSize) {
            return [[0, size - 1]];
        } else {
            let output = [];
            let i = 0;
            let lastIndex = -1;

            while (lastIndex < size - 1) {
                lastIndex = Math.min(i + chunkSize - 1, size - 1);
                let vec = [i, Math.min(i + chunkSize - 1, size - 1)];
                i += chunkSize;
                output.push(vec);
            }
            return output;
        }
    }

    static createIdMapping(iIds = []) {
        let idMapping = {};
        for (let i = 0; i < iIds.length; i++) {
            idMapping[iIds[i]] = Common.uuid();
        }
        return idMapping;
    }

    static chunkifyArray(array = [], chunkSize = 1) {
        let oChunks = [];

        const count = array.length;
        for (let i = 0; i < count; i += chunkSize) {
            oChunks.push(array.slice(i, i + chunkSize));
        }

        return oChunks;
    }

    static hasSessionExpiredMinutes(authTime, durationMinutes = 1) {
        // last sign in date
        const authDate = new Date(authTime * 1000);

        // expiration date
        let sessionExpirationDate = new Date(authDate);
        sessionExpirationDate.setMinutes(authDate.getMinutes() + durationMinutes);

        // Current date
        const currentDate = new Date();

        // debug purpose
        console.log(`
            ########################
                Common::hasSessionExpiredMinutes():
                    auth date: ${authDate}
                    session expiration date: ${sessionExpirationDate}
                    current date: ${currentDate}
            ########################`);

        return currentDate > sessionExpirationDate;
    }
    static hasSessionExpiredMonths(authTime, durationMonths = 1) {
        // last sign in date
        const authDate = new Date(authTime * 1000);

        // expiration date
        let sessionExpirationDate = new Date(authDate);
        sessionExpirationDate.setMonth(authDate.getMonth() + durationMonths);

        // Current date
        const currentDate = new Date();

        return currentDate > sessionExpirationDate;
    }

    static flatArray(iArray = []) {
        let flattenArray = [];

        const arrayLength = iArray.length;
        for (let i = 0; i < arrayLength; ++i) {
            flattenArray.push(...iArray[i]);
        }

        return flattenArray;
    }
}
